import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TimerState {
  isRunning: boolean;
  isPaused: boolean;
  startTime: number | null;      // timestamp when started
  pausedTime: number | null;     // timestamp when paused
  elapsedSeconds: number;        // accumulated time while paused
  projectId: string | null;
  categoryId: string | null;
  subprojectId: string | null;
  description: string;

  // Actions
  start: (projectId?: string, categoryId?: string, subprojectId?: string, description?: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => { hours: number; projectId: string | null; categoryId: string | null; subprojectId: string | null; description: string };
  discard: () => void;
  updateDescription: (description: string) => void;
  updateProject: (projectId: string) => void;
  updateCategory: (categoryId: string) => void;
  updateSubproject: (subprojectId: string) => void;
  getCurrentElapsed: () => number; // Returns current elapsed time in seconds
}

export const useTimerStore = create<TimerState>()(
  persist(
    (set, get) => ({
      isRunning: false,
      isPaused: false,
      startTime: null,
      pausedTime: null,
      elapsedSeconds: 0,
      projectId: null,
      categoryId: null,
      subprojectId: null,
      description: '',

      start: (projectId, categoryId, subprojectId, description) => {
        set({
          isRunning: true,
          isPaused: false,
          startTime: Date.now(),
          pausedTime: null,
          elapsedSeconds: 0,
          projectId: projectId || null,
          categoryId: categoryId || null,
          subprojectId: subprojectId || null,
          description: description || '',
        });
      },

      pause: () => {
        const state = get();
        if (!state.isRunning || state.isPaused) return;

        const now = Date.now();
        const additionalSeconds = state.startTime
          ? Math.floor((now - state.startTime) / 1000)
          : 0;

        set({
          isPaused: true,
          pausedTime: now,
          elapsedSeconds: state.elapsedSeconds + additionalSeconds,
          startTime: null,
        });
      },

      resume: () => {
        const state = get();
        if (!state.isRunning || !state.isPaused) return;

        set({
          isPaused: false,
          startTime: Date.now(),
          pausedTime: null,
        });
      },

      stop: () => {
        const state = get();
        const elapsed = get().getCurrentElapsed();
        const hours = elapsed / 3600; // Convert seconds to hours

        const result = {
          hours: elapsed > 0 ? Math.ceil(hours * 4) / 4 : 0, // Round UP to nearest .25 hour
          projectId: state.projectId,
          categoryId: state.categoryId,
          subprojectId: state.subprojectId,
          description: state.description,
        };

        // Reset timer
        set({
          isRunning: false,
          isPaused: false,
          startTime: null,
          pausedTime: null,
          elapsedSeconds: 0,
          projectId: null,
          categoryId: null,
          subprojectId: null,
          description: '',
        });

        return result;
      },

      discard: () => {
        set({
          isRunning: false,
          isPaused: false,
          startTime: null,
          pausedTime: null,
          elapsedSeconds: 0,
          projectId: null,
          categoryId: null,
          subprojectId: null,
          description: '',
        });
      },

      updateDescription: (description: string) => {
        set({ description });
      },

      updateProject: (projectId: string) => {
        set({ projectId, subprojectId: null });
      },

      updateCategory: (categoryId: string) => {
        set({ categoryId });
      },

      updateSubproject: (subprojectId: string) => {
        set({ subprojectId: subprojectId || null });
      },

      getCurrentElapsed: () => {
        const state = get();

        if (!state.isRunning) return 0;

        if (state.isPaused) {
          return state.elapsedSeconds;
        }

        if (state.startTime) {
          const now = Date.now();
          const currentSessionSeconds = Math.floor((now - state.startTime) / 1000);
          return state.elapsedSeconds + currentSessionSeconds;
        }

        return state.elapsedSeconds;
      },
    }),
    {
      name: 'mytime-timer-storage',
    }
  )
);
